<template>
  <div
    class="count-cell"
    :class="
      editing || partial ? 'd-flex flex-column align-items-end' : 'text-end'
    "
    @click.stop
    @mousedown.stop
  >
    <template v-if="editing">
      <input
        ref="editor"
        v-focus-select
        type="number"
        class="form-control form-control-sm text-end input-spin-none count-editor w-100 fw-semibold"
        :value="selected"
        min="1"
        :max="count"
        :aria-label="t('dashboard.portfolio.table.count_cell.editor')"
        @keydown.enter.prevent="editor?.blur()"
        @keydown.esc.prevent="editor?.blur()"
        @blur="commit"
      />
      <span class="count-of text-nowrap text-body-tertiary">{{
        ofCountLabel
      }}</span>
    </template>
    <template v-else>
      <InfoTooltip
        :text="
          t(
            partial
              ? 'dashboard.portfolio.table.count_cell.partial_title'
              : 'dashboard.portfolio.table.count_cell.full_title'
          )
        "
        :showIcon="false"
      >
        <button
          type="button"
          class="count-amount p-0 bg-transparent"
          :class="{
            'count-amount--partial text-warning-emphasis fw-bolder': partial,
          }"
          :aria-label="amountLabel"
          @click="$emit('edit')"
        >
          {{ formatNumberWithSpaces(partial ? selected : count) }}
        </button>
      </InfoTooltip>
      <span v-if="partial" class="count-of text-nowrap text-warning-emphasis">
        {{ ofCountLabel }}
      </span>
    </template>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import { isPartialCount } from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import { computed, useTemplateRef, type Directive } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The Count cell of a selected row on a count-editing table ("2A"): the selected amount as a button,
 * amber with "of {count}" under it when only part of the row is selected, or the amount editor while
 * `editing`. Unselected rows keep the table's plain number, so only selected rows mount this. Clicks
 * inside never reach the row, so opening or using the editor doesn't toggle the row's checkbox.
 */
const props = defineProps<{
  /** The row's whole amount, in MWh. */
  count: number;
  /** The MWh selected from the row. */
  selected: number;
  editing: boolean;
}>();

const emit = defineEmits<{
  edit: [];
  /** On blur (Enter and Escape blur too): the whole number typed, or null when there is none. */
  commit: [count: number | null];
}>();

const { t } = useI18n();
const editor = useTemplateRef("editor");

const partial = computed(() => isPartialCount(props.selected, props));

const amountLabel = computed(() =>
  partial.value
    ? t("dashboard.portfolio.table.count_cell.partial_label", {
        selected: formatNumberWithSpaces(props.selected),
        count: formatNumberWithSpaces(props.count),
      })
    : t("dashboard.portfolio.table.count_cell.full_label", {
        count: formatNumberWithSpaces(props.count),
      })
);

const ofCountLabel = computed(() =>
  t("dashboard.portfolio.table.count_cell.of", {
    count: formatNumberWithSpaces(props.count),
  })
);

// Reads the value off the event, not the template ref: a browser that blurs the focused input as its
// row unmounts (virtual scroll) does so after Vue has already cleared the ref.
const commit = ({ target }: FocusEvent) => {
  if (!(target instanceof HTMLInputElement)) return;
  const parsed = Number.parseInt(target.value, 10);
  emit("commit", Number.isNaN(parsed) ? null : parsed);
};

// Focused and selected on open so typing replaces the amount. A directive's mounted hook runs once
// the input is in the document; a function ref runs mid-patch, before it is.
const vFocusSelect: Directive<HTMLInputElement> = {
  mounted: (el) => {
    el.focus();
    el.select();
  },
};
</script>

<style scoped lang="scss">
// A selected amount is a button; a partial one is amber with "of {count}" under it.
.count-amount {
  border: 0;
  border-bottom: 1px dashed var(--phoenix-tertiary-color);
  color: inherit;
  font: inherit;
  line-height: 1.3;
  cursor: text;

  &--partial {
    border-bottom-color: var(--phoenix-warning);
  }
}

.count-of {
  font-size: 0.6875rem;
  line-height: 1.1;
}

// Fills the Count column, which fits a 7-digit amount; short enough for "of {count}" to fit under
// it in the table's ROW_HEIGHT_PX.
.count-editor {
  height: 22px;
  min-height: 0;
  padding: 0 0.375rem;
  font-size: 0.8125rem;
}
</style>
